import api from '../api';

export interface AdminConfiguration {
  id: number;
  config_type: string;
  config_value: string;
  display_order: number;
  is_active: boolean;
  metadata: any;
  created_at: string;
  updated_at: string;
}

export interface CreateConfigurationRequest {
  config_type: string;
  config_value: string;
  display_order?: number;
  is_active?: boolean;
  metadata?: any;
}

export interface UpdateConfigurationRequest {
  config_value?: string;
  display_order?: number;
  is_active?: boolean;
  metadata?: any;
}

class AdminConfigurationService {
  private readonly basePath = '/api/admin-configurations';

  async getConfigTypes(): Promise<string[]> {
    try {
      const response = await api.get(`${this.basePath}/types`);
      return response.data.data;
    } catch (error) {
      console.error('Failed to get config types:', error);
      throw error;
    }
  }

  async getByType(configType: string, activeOnly: boolean = true): Promise<AdminConfiguration[]> {
    try {
      const response = await api.get(`${this.basePath}/type/${configType}`, {
        params: { active_only: activeOnly }
      });
      return response.data.data;
    } catch (error) {
      console.error(`Failed to get configurations for type ${configType}:`, error);
      throw error;
    }
  }

  async getById(id: number): Promise<AdminConfiguration> {
    try {
      const response = await api.get(`${this.basePath}/${id}`);
      return response.data.data;
    } catch (error) {
      console.error(`Failed to get configuration ${id}:`, error);
      throw error;
    }
  }

  async create(configData: CreateConfigurationRequest): Promise<AdminConfiguration> {
    try {
      const response = await api.post(this.basePath, configData);
      return response.data.data;
    } catch (error) {
      console.error('Failed to create configuration:', error);
      throw error;
    }
  }

  async update(id: number, updateData: UpdateConfigurationRequest): Promise<AdminConfiguration> {
    try {
      const response = await api.put(`${this.basePath}/${id}`, updateData);
      return response.data.data;
    } catch (error) {
      console.error(`Failed to update configuration ${id}:`, error);
      throw error;
    }
  }

  async delete(id: number): Promise<void> {
    try {
      await api.delete(`${this.basePath}/${id}`);
    } catch (error) {
      console.error(`Failed to delete configuration ${id}:`, error);
      throw error;
    }
  }

  async createBulk(configurations: CreateConfigurationRequest[]): Promise<AdminConfiguration[]> {
    try {
      const response = await api.post(`${this.basePath}/bulk`, {
        configurations
      });
      return response.data.data;
    } catch (error) {
      console.error('Failed to create configurations in bulk:', error);
      throw error;
    }
  }

  async updateBulk(configurations: (UpdateConfigurationRequest & { id: number })[]): Promise<AdminConfiguration[]> {
    try {
      const response = await api.put(`${this.basePath}/bulk`, {
        configurations
      });
      return response.data.data;
    } catch (error) {
      console.error('Failed to update configurations in bulk:', error);
      throw error;
    }
  }

  async reorder(configType: string, order: { id: number; display_order: number }[]): Promise<AdminConfiguration[]> {
    try {
      const response = await api.put(`${this.basePath}/type/${configType}/reorder`, {
        order
      });
      return response.data.data;
    } catch (error) {
      console.error(`Failed to reorder configurations for type ${configType}:`, error);
      throw error;
    }
  }

  async toggleActive(id: number): Promise<AdminConfiguration> {
    try {
      const response = await api.patch(`${this.basePath}/${id}/toggle-active`);
      return response.data.data;
    } catch (error) {
      console.error(`Failed to toggle active status for configuration ${id}:`, error);
      throw error;
    }
  }

  async getActiveValuesForType(configType: string): Promise<Array<{id: number; value: string; metadata: any}>> {
    try {
      const configurations = await this.getByType(configType, true);
      return configurations.map(config => ({
        id: config.id,
        value: config.config_value,
        metadata: config.metadata
      }));
    } catch (error) {
      console.error(`Failed to get active values for type ${configType}:`, error);
      throw error;
    }
  }

  async searchConfigurations(searchTerm: string, configType?: string): Promise<AdminConfiguration[]> {
    try {
      const response = await api.get(`${this.basePath}/search`, {
        params: { 
          search: searchTerm,
          config_type: configType
        }
      });
      return response.data.data;
    } catch (error) {
      console.error('Failed to search configurations:', error);
      throw error;
    }
  }
}

const adminConfigurationServiceInstance = new AdminConfigurationService();
export { adminConfigurationServiceInstance as AdminConfigurationService };
export default adminConfigurationServiceInstance;